import { Component, inject, input, OnChanges, OnDestroy, OnInit, output, signal, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { faClose } from '@fortawesome/pro-regular-svg-icons';

import { filter, map, Observable, startWith, Subject, takeUntil } from 'rxjs';
import {
  AddCondition,
  BooleanOperators,
  Command,
  Condition,
  NumberOperators,
  QuestionType,
  RatingType,
  SingleChoiceOperators,
  TextOperators
} from '@empathy/survey/base';
import { OperatorText, Question } from '@empathy/survey-core';

import { ConditionalLogicByQuestionTypeSettings, ConditionOperatorFormSettings } from '../../../models/conditions/condition-operator-form-settings';
import { QuestionTypeConfig } from '../../../models/question-type-config.constants';
import { SurveyConditionalsService } from '../../../services/survey-conditionals.service';
import { SurveyEditorService } from '../../../services/survey-editor.service';

@Component({
  selector: 'empathy-conditional-logic-form',
  templateUrl: 'conditional-logic-form.component.html',
  styleUrls: ['./conditional-logic-form.component.scss', './_conditional-logic-form-theme.component.scss']
})
export class ConditionalLogicFormComponent implements OnInit, OnChanges, OnDestroy {
  isFirstCondition = input<boolean>(false);
  previousQuestions = input<Question[]>([]);
  cancel = output<void>();
  save = output<AddCondition>();

  conditionOperatorFormSettings = ConditionOperatorFormSettings;
  conditionalLogicByQuestionTypeSettings = ConditionalLogicByQuestionTypeSettings;

  filteredQuestions$: Observable<Question[]>;
  invalidCondition$ = signal<boolean>(false);
  valuesApplied$ = signal<boolean>(false);

  commandSelected: Command = 'AND';
  faClose = faClose;
  filterCtrl = new FormControl('');
  operators = OperatorText;
  questionTypeConfig = QuestionTypeConfig;

  surveyConditionalsService = inject(SurveyConditionalsService);

  private destroyed = new Subject<void>();

  private surveyEditorService = inject(SurveyEditorService);

  ngOnInit(): void {
    this.filteredQuestions$ = this.filterCtrl.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value) => this.filter(value)),
      takeUntil(this.destroyed)
    );

    this.surveyEditorService.questionAdded$.pipe(takeUntil(this.destroyed)).subscribe(() => this.onClearQuestionSelected());
    this.surveyConditionalsService
      .conditionForm$()
      .valueChanges.pipe(takeUntil(this.destroyed))
      .subscribe((values) => {
        this.valuesApplied$.set(BooleanOperators.includes(values.operator));
        this.invalidCondition$.set(false);
        this.surveyConditionalsService.conditionFormValues$.set(values);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.previousQuestions?.firstChange) {
      this.filterCtrl.reset();
      this.onClearQuestionSelected();
    }
  }

  ngOnDestroy(): void {
    this.onClearQuestionSelected();
    this.destroyed.next();
    this.destroyed.complete();
  }

  onApply(): void {
    if (
      this.surveyConditionalsService.questionSelected$().type === QuestionType.RankSort &&
      this.surveyConditionalsService.questionSelected$().ratingType === RatingType.numeric
    ) {
      this.surveyConditionalsService.onUpdateSortRankNumericValues();
    }

    if (SingleChoiceOperators.includes(this.surveyConditionalsService.conditionForm$().get('operator').value)) {
      let valueToMatch = this.surveyConditionalsService.conditionForm$().get('valueToMatch').value;
      valueToMatch = !Array.isArray(valueToMatch) ? [valueToMatch] : valueToMatch;
      this.surveyConditionalsService.conditionForm$().get('valueToMatch').setValue(valueToMatch);
    } else if (NumberOperators.includes(this.surveyConditionalsService.conditionForm$().get('operator').value)) {
      this.surveyConditionalsService.onUpdateNumberOperatorValues();
    } else if (TextOperators.includes(this.surveyConditionalsService.conditionForm$().get('operator').value)) {
      this.surveyConditionalsService.onUpdateTextOperatorValues();
    }

    this.valuesApplied$.set(true);
  }

  onSave(): void {
    this.invalidCondition$.set(false);

    if (!BooleanOperators.includes(this.surveyConditionalsService.conditionForm$().get('operator').value)) {
      this.surveyConditionalsService.conditionForm$().markAllAsTouched();
      if (this.surveyConditionalsService.conditionForm$().invalid) {
        this.invalidCondition$.set(true);
        return;
      }
    }

    const condition = this.getConditionForSave();

    this.save.emit({ condition, command: this.commandSelected });
  }

  onCancel(): void {
    this.onClearQuestionSelected();
    this.cancel.emit();
  }

  onSelectedQuestion(event: MatAutocompleteSelectedEvent): void {
    this.surveyConditionalsService.questionSelected$.set(event.option.value);
    this.surveyConditionalsService.conditionForm$().get('questionId').setValue(event.option.value.id);
  }

  onClearValuesSelected(index?: number): void {
    const values = this.surveyConditionalsService.conditionForm$().get('valueToMatch').value;
    values.splice(index, 1);
    if (values.length > 0) {
      this.surveyConditionalsService
        .conditionForm$()
        .get('valueToMatch')
        .setValue(this.joinValues() ? [values.join(';')] : values);
    } else {
      this.surveyConditionalsService.conditionForm$().get('valueToMatch').reset();
    }
  }

  onClearQuestionSelected(): void {
    this.filterCtrl.reset();
    this.surveyConditionalsService.questionSelected$.set(null);
    this.surveyConditionalsService.conditionForm$().reset();
  }

  private getConditionForSave(): Condition {
    switch (this.surveyConditionalsService.questionSelected$().type) {
      case QuestionType.Ranking:
        const valueToMatch = this.surveyConditionalsService
          .conditionForm$()
          .get('valueToMatch')
          .value.map((val) => this.surveyConditionalsService.questionSelected$().options.find((opt) => opt.id === val).value);

        return { ...this.surveyConditionalsService.conditionForm$().value, valueToMatch } as Condition;

      default:
        return this.surveyConditionalsService.conditionForm$().value as Condition;
    }
  }

  private filter(value: string): Question[] {
    if (!value) {
      return this.previousQuestions().slice();
    }

    const filterValue = value.toLowerCase();
    return this.previousQuestions().filter((option) => option.code?.toLowerCase().includes(filterValue) || option.title?.toLowerCase().includes(filterValue));
  }

  private joinValues(): boolean {
    return ['NOT_CONTAINS', 'CONTAINS'].includes(this.surveyConditionalsService.conditionFormValues$().operator);
  }
}
